import { ReactElement } from 'react'
import { AxiosResponse } from 'axios'
import useQueueDispatch from '../queue/useQueueDispatch'
import { QueueCallbacks, QueueJobKind } from '../queue/queueTypes'
import { DocumentGenerationContextData, DocumentGenerationType } from './documentGenerationTypes'

export type SubmitDocumentGeneration<RequestT, StatusT> = (submission: {
    /**
     * Identifies the job. Reusing an id while the previous job is still running is reported back as a
     * duplicate, so pass something stable and specific — the id of the thing being generated — when the
     * screen wants to know whether a generation for it is in flight.
     */
    requestId: string
    data: RequestT
    title?: ReactElement | string
    callbacks?: QueueCallbacks<RequestT, StatusT, DocumentGenerationContextData>
}) => void

/**
 * Dispatches a document generation request. This is the one place that knows how to talk to the
 * generate endpoint; the hooks around it only decide which id and title a job gets.
 */
export default function useSubmitDocumentGeneration<RequestT, StatusT = {}>(
    generationType: DocumentGenerationType,
): SubmitDocumentGeneration<RequestT, StatusT> {
    const dispatch = useQueueDispatch<RequestT, StatusT, DocumentGenerationContextData>(QueueJobKind.Document)

    return ({ requestId, data, title, callbacks }) => {
        dispatch(requestId, {
            // The document generate endpoint produces only a UUID that represents the Azure SB message ID.
            mapDispatchResponse: (response: AxiosResponse<string>) => ({
                messageId: response.data,
                driveItem: undefined,
            }),
            generationType,
            url: '/document/generate',
            data,
            title,
            callbacks,
        })
    }
}
